// Import components
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { CardModule } from 'primeng/card';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { StorageSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/storage-single-select/storage-single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';

// Import pipes
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';

// Import services
import { BucketService } from '@zaoblako/web/core/services/storage/bucket.service';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';

// Import types
import { BucketFormDto, type BucketFormInterface } from '@zaoblako/shared/modules/storage/dtos/bucket/bucket-form.dto';
import type { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import type { BucketFindInterface } from '@zaoblako/shared/modules/storage/dtos/bucket/bucket-find.dto';

// Import externals
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, type OnInit } from '@angular/core';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-bucket-form',
  templateUrl: './bucket-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    InputTextComponent,
    StorageSingleSelectComponent,
    SpinnerComponent,
    CheckboxModule,
    ButtonComponent,
    JoinPipe
  ]
})
export class BucketFormComponent extends BaseFormComponent<BucketFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public bucket = <BucketFindInterface>{};

  public loading$: Observable<boolean>;

  public dto = BucketFormDto;
  public form$: Observable<FormType>;

  public constructor(
    public readonly formStoreService: FormStoreService,
    private readonly bucketService: BucketService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.bucketService
      .findById<BucketFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withDeleted: true
      })
      .subscribe({
        next: (storage) => {
          this.bucket = storage;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveBucket(): void {
    if (this.checkValidate(this.bucket).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.bucketService.update<BucketFormInterface, BucketFindInterface>(this.bucket).subscribe({
          next: (admin) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Корзина S3 "${this.bucket.name}" обновлена!`);
            this.formStoreService.saveNext(admin, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.bucketService.create<BucketFindInterface>(this.bucket).subscribe({
          next: (admin) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Корзина S3 создана!');
            this.formStoreService.saveNext(admin, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteBucket(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить корзину S3 "${this.bucket.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.bucketService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Корзина S3 "${this.bucket.name}" удалена!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
